/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ActionCreator,
  PayloadAction,
  PayloadActionCreator,
  createAction,
} from '@reduxjs/toolkit';

import { IsUnknown } from '@tdp/common/shared/tsUtils';

import { CommandHandler, CommandHandlerCommandPayload } from './abstractions';

type SingleMapperCallback = (
  payload: any,
  getState: () => any
) => PromiseLike<PayloadAction<any>> | PayloadAction<any>;
type SingleMapperHandlerCommandPayload<
  TSingleMapperFn extends SingleMapperCallback,
> = TSingleMapperFn extends (payload: infer P, getState: () => any) => any
  ? IsUnknown<P, void, P>
  : never;
type SingleMapperHandlerEvent<TSingleMapperFn extends SingleMapperCallback> =
  TSingleMapperFn extends (payload: any, getState: () => any) => infer R
  ? R extends undefined
  ? undefined
  : Awaited<R>
  : never;
type SingleMapperHandlerState<TSingleMapperFn extends SingleMapperCallback> =
  TSingleMapperFn extends (payload: any, state: infer S) => any ? S : never;
type SingleMapperHandlerCommandHandler<
  TSingleMapperFn extends SingleMapperCallback,
> = CommandHandler<
  SingleMapperHandlerCommandPayload<TSingleMapperFn>,
  SingleMapperHandlerEvent<TSingleMapperFn>,
  SingleMapperHandlerState<TSingleMapperFn>
>;
type IterableElement<TIterable extends Iterable<any> | AsyncIterable<any>> =
  TIterable extends Iterable<infer I>
  ? I
  : TIterable extends AsyncIterable<infer I>
  ? I
  : never;
type IteratedMapperCallback = (
  payload: any,
  getState: () => any
) => Iterable<PayloadAction<any>> | AsyncIterable<PayloadAction<any>>;
type IteratedMapperCallbackCommandPayload<
  TIteratedFn extends IteratedMapperCallback,
> = TIteratedFn extends (payload: infer P, getState: () => any) => any
  ? IsUnknown<P, void, P>
  : never;
type IteratedMapperHandlerEvents<TIteratedFn extends IteratedMapperCallback> =
  TIteratedFn extends (payload: any, getState: () => any) => infer R
  ? R extends Iterable<any> | AsyncIterable<any>
  ? IterableElement<R>
  : never
  : never;
type IteratedMapperHandlerState<TIteratedFn extends IteratedMapperCallback> =
  TIteratedFn extends (payload: any, state: infer S) => any ? S : never;
type IteratedMapperCommandHandler<TIteratedFn extends IteratedMapperCallback> =
  CommandHandler<
    IteratedMapperCallbackCommandPayload<TIteratedFn>,
    IteratedMapperHandlerEvents<TIteratedFn>,
    IteratedMapperHandlerState<TIteratedFn>
  >;

function isIterable(o: any): o is Iterable<unknown> {
  return Symbol.iterator in o;
}
function isAsyncIterable(o: any): o is AsyncIterable<unknown> {
  return Symbol.asyncIterator in o;
}
export function isPayloadAction(o: unknown): o is PayloadAction<unknown> {
  return (
    !!o && typeof o === 'object' && 'type' in o && typeof o.type == 'string'
  );
}
function isPromiseLike(o: any): o is PromiseLike<unknown> {
  return 'then' in o;
}

export type CommandHandlerDeclaration =
  | SingleMapperCallback
  | IteratedMapperCallback;
export type InferredHandler<
  THandlerDeclaration extends CommandHandlerDeclaration,
> = THandlerDeclaration extends IteratedMapperCallback
  ? IteratedMapperCommandHandler<THandlerDeclaration>
  : THandlerDeclaration extends SingleMapperCallback
  ? SingleMapperHandlerCommandHandler<THandlerDeclaration>
  : never;
export type InferredHandlerEvents<
  THandlerDeclaration extends CommandHandlerDeclaration,
> = THandlerDeclaration extends IteratedMapperCallback
  ? IteratedMapperHandlerEvents<THandlerDeclaration>
  : THandlerDeclaration extends SingleMapperCallback
  ? SingleMapperHandlerEvent<THandlerDeclaration>
  : never;

export function adaptCommandDeclaration<Decl extends CommandHandlerDeclaration>(
  fn: Decl
): InferredHandler<Decl> {
  return function (command, api, onError) {
    const getState = api.getState.bind(api);
    const result = fn(command, getState);
    // throw it in a background executor promise
    async function results() {
      if (isAsyncIterable(result)) {
        for await (const evt of result) {
          api.dispatch(evt);
        }
      } else if (isIterable(result)) {
        for (const evt of result) {
          api.dispatch(evt);
        }
      } else if (isPayloadAction(result)) {
        api.dispatch(result);
      } else if (isPromiseLike(result)) {
        const evt = await result;
        api.dispatch(evt);
      }
    }
    //TODO(nibeauli): add mechanism for error handling
    void results().catch((e: unknown) => onError?.(e instanceof Error ? e : new Error(String(e))));
  } as InferredHandler<Decl>; // Worrying that this is needed.
}
export interface CommandHandlerOutput<
  TPrefix extends string,
  THandlers extends Record<string, CommandHandlerDeclaration>,
> {
  name: TPrefix;
  actions: {
    [Type in keyof THandlers]: Type extends string
    ? PayloadActionCreator<
      CommandHandlerCommandPayload<InferredHandler<THandlers[Type]>>,
      `${TPrefix}/${Type}`
    >
    : never;
  };
  caseHandlers: {
    [Type in keyof THandlers as Type extends string
    ? `${TPrefix}/${Type}`
    : never]: InferredHandler<THandlers[Type]>;
  };
}

export function createCommands<
  TPrefix extends string,
  THandlers extends Record<string, CommandHandlerDeclaration>,
>(options: {
  name: TPrefix;
  handlers: THandlers;
}): CommandHandlerOutput<TPrefix, THandlers> {
  const actions: Record<string, ActionCreator<any, any>> = {};
  const handlers: Record<string, CommandHandler<any, any>> = {};
  for (const [key, declaration] of Object.entries(options.handlers)) {
    const actionKey = `${options.name}/${key}`;
    actions[key] = createAction(actionKey);
    handlers[actionKey] = adaptCommandDeclaration(declaration);
  }

  return {
    name: options.name,
    actions: actions,
    caseHandlers: handlers,
  } as CommandHandlerOutput<TPrefix, THandlers>;
}
